// src/encounter/intelligence/rail-words.ts
//
// The Scrips Intelligence rail's fixed words and small pure helpers (round 18r design), kept apart from the components
// so each component file exports components only (react-refresh). No clinical sentence is written here: every clinical
// string on the rail is an engine field verbatim.
import type { ExplainSystem } from './use-explain';

/** The encounter phase the rail serves, as the header says it; on the workspace tabs, the screen's name. */
export type IntelligencePhase = 'Pre-visit' | 'Visit' | 'Wrap-up' | 'Chart' | 'Timeline' | 'Documents';

/** The workspace tabs the rail follows (`?x=intel-always-open`), in the rail lock's words (claude-os
 *  docs/intelligence-rail-lock-2026-11-04.md §3: "Pre-visit · Visit · Wrap-up · Chart · Timeline · Documents"). */
const TAB_PHASE: Readonly<Record<string, IntelligencePhase>> = { chart: 'Chart', timeline: 'Timeline', documents: 'Documents' };

/** The chip-routes screen (chip-routes.ts `Screen`) for a workspace tab. Documents has no screen of its own in Antonin's
 *  catalog: its chips ("Documents", "Read this") are the chart screen's. */
export const TAB_SCREEN: Readonly<Record<string, 'chart' | 'timeline'>> = { chart: 'chart', timeline: 'timeline', documents: 'chart' };

/**
 * The phase label: on the Patient chart, Timeline or Documents tab (only when the host passes `tab`, `?x=intel-always-open`),
 * the tab's name; before the encounter starts it is Pre-visit; once the note is signed, or on the Wrap-up step, it is
 * Wrap-up; otherwise Visit.
 */
export function intelligencePhase({
  started,
  signed,
  step,
  tab,
}: {
  started: boolean;
  signed: boolean;
  step?: string | null;
  tab?: string | null;
}): IntelligencePhase {
  if (tab && TAB_PHASE[tab]) return TAB_PHASE[tab];
  if (!started) return 'Pre-visit';
  if (signed || step === 'wrap-up') return 'Wrap-up';
  return 'Visit';
}

/** The screen the catalog rail serves (Samer, 4 Nov: the rail's label is the screen showing): the encounter phase on the
 *  Encounter tab, else the tab itself. */
export type RailScreenLabel = IntelligencePhase | 'Terrain' | 'Chart' | 'Timeline' | 'Documents';

const TAB_LABEL: Record<string, RailScreenLabel> = { terrain: 'Terrain', chart: 'Chart', timeline: 'Timeline', documents: 'Documents' };

/** The rail's label for the active header tab (`encounter` | `terrain` | `chart` | `timeline` | `documents`). */
export function railScreenLabel(tab: string | null | undefined, encounterPhase: IntelligencePhase): RailScreenLabel {
  return (tab && TAB_LABEL[tab]) || encounterPhase;
}


/** The body systems the engine explains (explainRoute.ts EXPLAIN_SYSTEMS), in its order, with the titles its answers carry.
 *  Samer, 4 Nov: all 7 open, as in the prototype; all 7 pass the engine's local checks on the real dev record. */
export const EXPLAIN_SYSTEMS: ReadonlyArray<{ id: ExplainSystem; label: string }> = [
  { id: 'heart-vessels', label: 'Heart & vessels' },
  { id: 'metabolism', label: 'Metabolism' },
  { id: 'liver-kidney-blood', label: 'Liver, kidney & blood' },
  { id: 'hormones', label: 'Hormones' },
  { id: 'inflammation-immunity', label: 'Inflammation & immunity' },
  { id: 'brain-nervous', label: 'Brain & nervous system' },
  { id: 'sleep-recovery-airway', label: 'Sleep, recovery & airway' },
];

/** The words for one system (the loading and refusal cards carry no engine title). */
export const systemLabel = (id: ExplainSystem): string => EXPLAIN_SYSTEMS.find((s) => s.id === id)?.label ?? EXPLAIN_SYSTEMS[0].label;

/** Fixed footer words (round 18r design). */
export const RAIL_WORDS = {
  explainChip: 'Explain a body system',
  lens: 'Retests',
  lensAria: 'Retests lens, menu: Orders, Patient facts, Justify, Coding check, Draft the plan',
  ask: 'Ask',
  askAria: 'Ask Scrips Intelligence',
  draftLine: 'Draft from Scrips Intelligence; not part of the record. Each line links to its source.',
  /** The Explain card's link under the results. */
  openResults: 'Open the results list',
  /** The Explain card's problem-list group label (round 18r). */
  problemsLabel: 'Problems',
  /** The Next step card's title. */
  nextStepTitle: 'Next best action',
} as const;

/** The page-header recording control's words (round 18r design). */
export const RECORDING_WORDS = {
  mic: 'Ambient capture',
  recording: 'Recording',
  starting: 'Starting…',
  stop: 'Stop',
} as const;

/** "m:ss" → "mm:ss" (the design's clock). */
export function clockOf(elapsed: string | undefined): string {
  if (!elapsed) return '00:00';
  return elapsed.length < 5 ? elapsed.padStart(5, '0') : elapsed;
}

/** A card's answer time, top right (round 18r): the clinic's local clock, 24 h. */
export function answerTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
